import React, { forwardRef } from "react";
import { FiPhone, FiMail } from "react-icons/fi";
import { FaGlobe } from "react-icons/fa";
import { GoLocation } from "react-icons/go";

const transactionTypeOptions = [
  { value: "cash_to_account", label: "Cash → Account" },
  { value: "account_to_cash", label: "Account → Cash" },
  { value: "account_to_account", label: "Account → Account" },
  { value: "check_to_account", label: "Check → Account" },
  { value: "check_to_cash", label: "Check → Cash" },
  { value: "account_to_check", label: "Account → Check" },
];

const typeLabels = { cash: "Cash", account: "Account", check: "Check" };

const fmt = (currency, amount) => {
  const num = Number(amount || 0);
  return `${currency || "BHD"} ${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const FinancialReportTemplate = forwardRef(({ data, type, school }, ref) => {
  const {
    nameOfSchool = "YOUR SCHOOL",
    schoolName,
    logo = "",
    contactDetails = {},
    address = "",
    currency = "BHD",
  } = school || {};

  const displayName = nameOfSchool !== "YOUR SCHOOL" ? nameOfSchool : schoolName || "YOUR SCHOOL";
  
  // 1. Updated Report Title logic to include Balance Sheet
  const reportTitle =
    type === "income"
      ? "INCOME STATEMENT REPORT"
      : type === "balancesheet"
      ? "BALANCE SHEET REPORT"
      : "CASH FLOW STATEMENT";

  return (
    <div
      ref={ref}
      className="bg-white mx-auto w-full relative flex flex-col justify-between"
      style={{ direction: "ltr", textAlign: "left", minHeight: "290mm" }}
    >
      {/* HEADER — same as invoice */}
      <div className="flex w-full flex-row gap-8 mb-2 pt-4 px-8">
        <div className="flex flex-col gap-2 w-[70%]">
          <div className="w-[99%] h-[3rem] bg-[#7F31AB] relative overflow-hidden">
            <div className="w-10 h-40 bg-white rotate-45 absolute end-2 top-[-70%]" />
          </div>
          <div className="w-[90%] h-[1.5rem] bg-[#8C1E60]" />
        </div>
        <div className="flex-1 flex flex-row gap-4 justify-end items-center h-[5rem]">
          {logo && (
            <img src={logo} alt="Logo" className="w-10 h-10 rounded-full object-contain" />
          )}
          <h1 className="font-bold text-base uppercase text-gray-800">
            {displayName}
          </h1>
        </div>
      </div>

      {/* TITLE */}
      <div className="flex w-full items-center justify-center font-bold text-2xl mt-6 mb-4 text-[#8C1E60] tracking-wider uppercase">
        {reportTitle}
      </div>

      {/* META INFO */}
      <div className="px-10 text-sm text-gray-700 flex justify-between mb-4">
        <div className="space-y-1">
          <p>
            <strong>Report Period:</strong> {data?.reportPeriod || "—"}
          </p>
          <p>
            <strong>Currency:</strong> {currency}
          </p>
        </div>
        <div className="text-right">
          <p>
            <strong>Generated:</strong> {data?.generatedAt || "—"}
          </p>
        </div>
      </div>

      {/* CONTENT */}
      <div className="px-10 pb-10">
        
        {/* ——— INCOME STATEMENT ——— */}
        {type === "income" && (
          <div className="border border-gray-200 text-sm rounded overflow-hidden">
            <div className="bg-[#7F31AB] text-white p-3 font-semibold uppercase tracking-wide text-xs">
              Detailed Breakdown
            </div>
            {/* Operating Revenue */}
            <div className="flex justify-between items-center px-4 py-3 bg-gray-50 font-semibold text-gray-800 border-b border-gray-100">
              <span>Operating Revenue</span>
              <span>{fmt(currency, data?.totalRevenue)}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-500 border-b border-gray-100 bg-[#F5F0FF]">
              <span>• Other Income</span>
              <span>{fmt(currency, 0)}</span>
            </div>
            {/* Operating Expenses */}
            <div className="flex justify-between items-center px-4 py-3 bg-gray-50 font-semibold text-red-500 border-b border-gray-100">
              <span>Operating Expenses</span>
              <span>({fmt(currency, data?.totalExpenses)})</span>
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-500 border-b border-gray-100 bg-[#E4D9FA]">
              <span>• Payroll &amp; Benefits</span>
              <span>{fmt(currency, data?.payroll)}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-500 border-b border-gray-100 bg-[#E4D9FA]">
              <span>• General Expenses</span>
              <span>{fmt(currency, data?.generalExpenses)}</span>
            </div>
            {/* Net Operating Income */}
            <div className="flex justify-between items-center px-4 py-4 font-bold text-base border-t-2 border-gray-300 text-gray-900 bg-gray-100">
              <span>Net Operating Income</span>
              <span className={Number(data?.netIncome) >= 0 ? "text-green-700" : "text-red-600"}>
                {fmt(currency, data?.netIncome)}
              </span>
            </div>
          </div>
        )}

        {/* ——— BALANCE SHEET ——— */}
        {/* 2. Added Balance Sheet Render Block */}
        {type === "balancesheet" && data?.balanceSheet && (
          <div className="border border-gray-200 text-sm rounded overflow-hidden">
            {/* ASSETS */}
            <div className="bg-[#7F31AB] text-white p-2 font-semibold uppercase tracking-wide text-xs">
              ASSETS
            </div>
            <div className="px-4 py-2 bg-gray-50 font-bold text-gray-700 border-b border-gray-200">
              I. Current Assets
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-600 border-b border-gray-100">
              <span>Cash & Bank Balances</span>
              <span>{fmt(currency, data.balanceSheet.assets?.currentAssets?.totalCashAndBank)}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-600 border-b border-gray-100">
              <span>Accounts Receivable (Unpaid)</span>
              <span>{fmt(currency, data.balanceSheet.assets?.currentAssets?.receivables?.totalUnpaid)}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-600 border-b border-gray-100">
              <span>Inventory (at cost)</span>
              <span>{fmt(currency, data.balanceSheet.assets?.currentAssets?.inventory?.total)}</span>
            </div>
            
            <div className="px-4 py-2 bg-gray-50 font-bold text-gray-700 border-y border-gray-200">
              II. Non-Current Assets
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-600 border-b border-gray-100">
              <span>Property, Plant & Equipment</span>
              <span>{fmt(currency, data.balanceSheet.assets?.nonCurrentAssets?.fixedAssets?.total)}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-4 font-bold text-base border-t-2 border-gray-300 text-emerald-800 bg-emerald-50">
              <span>TOTAL ASSETS</span>
              <span>{fmt(currency, data.balanceSheet.assets?.totalAssets)}</span>
            </div>

            {/* LIABILITIES & EQUITY */}
            <div className="bg-[#8C1E60] text-white p-3 font-semibold uppercase tracking-wide text-xs mt-6">
              LIABILITIES & EQUITY
            </div>
            
            <div className="px-4 py-2 bg-gray-50 font-bold text-gray-700 border-b border-gray-200">
              I. Equity (Shareholders' Funds)
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-600 border-b border-gray-100">
              <span>Total Equity / Investments</span>
              <span>{fmt(currency, data.balanceSheet.liabilitiesAndEquity?.equity?.totalEquity)}</span>
            </div>

            <div className="px-4 py-2 bg-gray-50 font-bold text-gray-700 border-y border-gray-200">
              II. Non-Current Liabilities
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-600 border-b border-gray-100">
              <span>Long-term Loans</span>
              <span>{fmt(currency, data.balanceSheet.liabilitiesAndEquity?.nonCurrentLiabilities?.loans?.total)}</span>
            </div>

            <div className="px-4 py-2 bg-gray-50 font-bold text-gray-700 border-y border-gray-200">
              III. Current Liabilities
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-600 border-b border-gray-100">
              <span>Expenses Payable (General)</span>
              <span>{fmt(currency, data.balanceSheet.liabilitiesAndEquity?.currentLiabilities?.generalExpensesPayable)}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-600 border-b border-gray-100">
              <span>Salary Payable</span>
              <span>{fmt(currency, data.balanceSheet.liabilitiesAndEquity?.currentLiabilities?.salaryPayable)}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-2 pl-8 text-gray-600 border-b border-gray-100">
              <span>Vouchers / Cash Transfers</span>
              <span>{fmt(currency, data.balanceSheet.liabilitiesAndEquity?.currentLiabilities?.vouchers?.total)}</span>
            </div>

            <div className="flex justify-between items-center px-4 py-4 font-bold text-base border-t-2 border-gray-300 text-rose-800 bg-rose-50">
              <span>TOTAL LIABILITIES & EQUITY</span>
              <span>{fmt(currency, data.balanceSheet.liabilitiesAndEquity?.totalLiabilitiesAndEquity)}</span>
            </div>
          </div>
        )}

        {/* ——— CASH FLOW ——— */}
        {type === "cashflow" && (
          <table className="w-full text-xs mt-2 border-collapse">
            <thead>
              <tr className="bg-[#7F31AB] text-white">
                <th className="p-2 border-4 border-white text-left">Type</th>
                <th className="p-2 border-4 border-white text-left">From</th>
                <th className="p-2 border-4 border-white text-left">To</th>
                <th className="p-2 border-4 border-white text-right">Amount</th>
                <th className="p-2 border-4 border-white text-left">Date</th>
                <th className="p-2 border-4 border-white text-left">Note</th>
              </tr>
            </thead>
            <tbody>
              {(data?.flows || []).map((row, i) => {
                const opt = transactionTypeOptions.find(
                  (o) => o.value === row.transactionType
                );
                return (
                  <tr key={i} className="bg-[#E4D9FA]">
                    <td className="p-2 border-4 border-white">
                      {opt?.label || row.transactionType || "—"}
                    </td>
                    <td className="p-2 border-4 border-white">
                      {typeLabels[row?.from?.type] || row?.from?.type || "—"}
                      {row?.from?.entityId?.entityName
                        ? ` / ${row.from.entityId.entityName}`
                        : ""}
                    </td>
                    <td className="p-2 border-4 border-white">
                      {typeLabels[row?.to?.type] || row?.to?.type || "—"}
                      {row?.to?.entityId?.entityName
                        ? ` / ${row.to.entityId.entityName}`
                        : ""}
                    </td>
                    <td className="p-2 border-4 border-white text-right font-semibold">
                      {row.amount != null
                        ? Number(row.amount).toLocaleString()
                        : "—"}
                    </td>
                    <td className="p-2 border-4 border-white">
                      {row.transactionDate?.slice(0, 10) || "—"}
                    </td>
                    <td className="p-2 border-4 border-white text-gray-600">
                      {row.note || "—"}
                    </td>
                  </tr>
                );
              })}
              {(!data?.flows || data.flows.length === 0) && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-gray-400 border-4 border-white">
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* FOOTER — same as invoice */}
      <div className="bg-[#89125B] text-white p-6 relative overflow-hidden mt-auto">
        <div className="w-40 h-40 bg-purple-500 rotate-45 absolute end-[-15%] top-[-60%]" />
        <div className="w-full flex flex-row items-center justify-around">
          <div className="flex flex-col items-start gap-4">
            <div className="flex items-center gap-2 text-sm">
              <FiPhone className="text-lg" />
              <span>{contactDetails?.contactNumber || "—"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FiMail className="text-lg" />
              <span>{contactDetails?.email || "—"}</span>
            </div>
          </div>
          <div className="flex flex-col items-start gap-4">
            <div className="flex items-center gap-2 text-sm">
              <FaGlobe className="text-lg" />
              <span>{contactDetails?.website || "—"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <GoLocation className="text-lg" />
              <span>{address || "—"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default FinancialReportTemplate;